// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import usePersistedState from '../utils/mapPersistedState';
import { Stack, Grid, Box } from "@mui/material";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Oval } from 'react-loader-spinner';
import { Bar } from "react-chartjs-2";
import { apiRequest } from "../../services/utils/HttpUtils";
import { useQuery } from "@tanstack/react-query";
import { getUserInfo } from "../../graphql/queries_user_info";
import totaliser from "./totaliser";
import htmlLegend from "./htmlLegend";
import { RetrieveNonLabourValueTonnage } from "../../graphql/queries_nationalMap";
import MapPlotFilterList from "../utils/mapPlotFilterList";
import {
    nonLabourValueFiltersConfigList,
    nonLabourTonnageFiltersConfigList
} from "./nationalMapFiltersConfig";

export default function NonLabourDemandChart({ queryVariables, allMapLists, setNonLabourPlotQueryProps, nonLabourPlotQueryProps, applyUpdates, setNonLabourTab, updateNonLabourCharts, updateAlert }) {

    // Authentication
    const { instance, accounts } = useMsal();
    const { data: userInfo } = getUserInfo();

    const valueLegendContainer = useRef();
    const tonnageLegendContainer = useRef();
    const nonLabourChartLoader = useRef();

    // const [nonLabourPlotQueryProps, setNonLabourPlotQueryProps] = usePersistedState<any>('nonLabourPlotQueryProps', {
    //     inPLEMGroup: ["ALL"],
    //     inResourceGroup: ["ALL"]
    // });

    const nonlabour_colours = [{ "resourcegroup": "Aggregate", "colour": "#4e79a7" }, { "resourcegroup": "Aluminium", "colour": "#a0cbe8" }, { "resourcegroup": "Asphalt", "colour": "#f28e2b" }, { "resourcegroup": "Bitumen Binders", "colour": "#ffbe7d" }, { "resourcegroup": "Bricks ", "colour": "#59a14f" }, { "resourcegroup": "Building Services - electrical equipment", "colour": "#8cd17d" }, { "resourcegroup": "Building Services - machanical equipment", "colour": "#b6992d" }, { "resourcegroup": "Bulldozers", "colour": "#f1ce63" }, { "resourcegroup": "Cement", "colour": "#8cd17d" }, { "resourcegroup": "Compactor", "colour": "#86bcb6" }, { "resourcegroup": "Control Equipment", "colour": "#e15759" }, { "resourcegroup": "Copper", "colour": "#b6992d" }, { "resourcegroup": "Electrical Bulk", "colour": "#f1ce63" }, { "resourcegroup": "Electrical Equipment", "colour": "#bab0ac" }, { "resourcegroup": "Excavators", "colour": "#d37295" }, { "resourcegroup": "Fiberglass", "colour": "#499894" }, { "resourcegroup": "Girders", "colour": "#86bcb6" }, { "resourcegroup": "Graders", "colour": "#d4a6c8" }, { "resourcegroup": "Kitchen Fitout - electrical equipment", "colour": "#9d7660" }, { "resourcegroup": "Linemarking & Road Furnitures", "colour": "#e15759" }, { "resourcegroup": "Mechanical Equipment", "colour": "#4e79a7" }, { "resourcegroup": "Mobile Cranes", "colour": "#a0cbe8" }, { "resourcegroup": "Plasterboard", "colour": "#ff9d9a" }, { "resourcegroup": "Plastics and Polymeric materials", "colour": "#79706e" }, { "resourcegroup": "PV Panels", "colour": "#bab0ac" }, { "resourcegroup": "Rail Track", "colour": "#d37295" }, { "resourcegroup": "Rock/Bluestone", "colour": "#fabfd2" }, { "resourcegroup": "Sand", "colour": "#b07aa1" }, { "resourcegroup": "Scaffold (tubular)", "colour": "#499894" }, { "resourcegroup": "Scissor Lifts", "colour": "#ff9d9a" }, { "resourcegroup": "Site Offices, Lunchrooms", "colour": "#79706e" }, { "resourcegroup": "Speciality Plant", "colour": "#fabfd2" }, { "resourcegroup": "Steel Re-inforcement", "colour": "#d4a6c8" }, { "resourcegroup": "Steel - Structural Elements", "colour": "#9d7660" }, { "resourcegroup": "Street Sweeper", "colour": "#b07aa1" }, { "resourcegroup": "Timber", "colour": "#d7b5a6" }, { "resourcegroup": "Tower Cranes", "colour": "#d7c5af" }, { "resourcegroup": "WC", "colour": "#f28e2b" }];

    const nonLabourValuesRef = useRef<any[]>([]);
    const nonLabourTonnageRef = useRef<any[]>([]);

    const { data: nonLabourValuesTonnage, status: nonLabourStatus, fetchStatus: nonLabourFetchStatus, error: nonLabourError, refetch: refetchNonLabourValuesTonnage } = useQuery({
        queryKey: ["NonLabourValuesTonnage"],
        queryFn: () => {
            return apiRequest(
                {
                    query: RetrieveNonLabourValueTonnage,
                    variables: queryVariables,
                    instance: instance,
                    accounts: accounts
                })
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        cacheTime: Infinity,
        enabled: !!userInfo
    });

    // Start - non labour value chart
    // Set the non labour value chart options
    const nonlabour_value_options = {
        layout: {
            padding: {
                top: 20
            }
        },
        plugins: {
            htmlLegend: {
                container: valueLegendContainer
            },
            title: {
                display: false,
                text: 'Non-labour demand in value ($)'
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        let label = tooltipItem.dataset.label;
                        return label + ': ' + new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", currencyDisplay: "narrowSymbol", notation: "compact", compactDisplay: "short" }).format(tooltipItem.parsed.y);
                    }
                }
            },
            legend: {
                display: false
            },
            datalabels: {
                anchor: 'end',
                align: 'end',
                offset: 0,
                formatter: (value, ctx) => {
                    const total = ctx.chart.$totalizer.totals[ctx.dataIndex];
                    return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", currencyDisplay: "narrowSymbol", notation: "compact", compactDisplay: "short" }).format(total);
                },
                display: function (ctx) {
                    return ctx.datasetIndex === ctx.chart.$totalizer.utmost
                }
            }
        },
        responsive: false,
        aspectRatio: 1,
        scales: {
            x: {
                stacked: true
            },
            y: {
                stacked: true,
                ticks: {
                    callback: (value, index, ticks) => {
                        return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", currencyDisplay: "narrowSymbol", notation: "compact", compactDisplay: "short" }).format(value);
                    }
                }
            }
        }
    };

    // Build the chart datasets dynamically
    const value_resource_groups = [...new Set(nonLabourValuesRef.current.map(item => item.ResourceGroup))];
    const years = [...new Set(nonLabourValuesRef.current.map(item => item.Year))];
    // Not sure if there is a cleaner way to
    // write the following code. Essentially it
    // is adding missing Year/ResourceGroup data.
    // The chart will fail to render correctly if
    // the missing data is not there.
    // Note: this code solution only applies to 
    // stacked bar chart.
    years.forEach(year => {
        value_resource_groups.forEach(resource => {
            const exists = nonLabourValuesRef.current.find(i => i.Year === year && i.ResourceGroup === resource);
            if (!exists) {
                nonLabourValuesRef.current.push({
                    ResourceGroup: resource,
                    Year: year,
                    Value: 0
                });
            }
        });
    });

    // Sort the results from the above
    nonLabourValuesRef.current.sort((a, b) => {
        return a.Year - b.Year || a.ResourceGroup.localeCompare(b.ResourceGroup);
    });
    
    // Build the datasets
    const nonlabour_value_datasets = [...new Set(value_resource_groups.map(resource_group => {
        return {
            label: resource_group,
            data: nonLabourValuesRef.current.filter((obj) => obj.ResourceGroup === resource_group).map((filObj) => filObj.Value),
            backgroundColor: nonlabour_colours.find(x => x.resourcegroup === resource_group)?.colour
        }
    }))];

    // Sort the data by the value amount
    // and create the unique years array
    nonlabour_value_datasets.sort((a, b) => Math.max(...b.data) - Math.max(...a.data));
    const nonlabour_value_chart = {
        labels: years,
        datasets: nonlabour_value_datasets
    };
    // End - non labour value chart

    // Start - non labour tonnage chart
    // Set the non labour tonnage chart options
    const nonlabour_tonnage_options = {
        layout: {
            padding: {
                top: 20
            }
        },
        plugins: {
            htmlLegend: {
                container: tonnageLegendContainer
            },
            title: {
                display: false,
                text: 'Non-labour demand in tonnage'
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        let label = tooltipItem.dataset.label;
                        return label + ': ' + new Intl.NumberFormat("en-AU", { notation: "compact", compactDisplay: "short" }).format(tooltipItem.parsed.y);
                    }
                }
            },
            legend: {
                display: false
            },
            datalabels: {
                anchor: 'end',
                align: 'end',
                offset: 0,
                formatter: (value, ctx) => {
                    const total = ctx.chart.$totalizer.totals[ctx.dataIndex]
                    return new Intl.NumberFormat("en-AU", { notation: "compact", compactDisplay: "short" }).format(total);
                },
                display: function (ctx) {
                    return ctx.datasetIndex === ctx.chart.$totalizer.utmost
                }
            }
        },
        responsive: false,
        aspectRatio: 1,
        scales: {
            x: {
                stacked: true
            },
            y: {
                stacked: true,
                ticks: {
                    callback: (value, index, ticks) => {
                        return new Intl.NumberFormat("en-AU", { notation: "compact", compactDisplay: "short" }).format(value);
                    }
                }
            }
        }
    };

    // Build the chart datasets dynamically
    const tonnage_resource_groups = [...new Set(nonLabourTonnageRef.current.map(item => item.ResourceGroup))];
    const tonnage_years = [...new Set(nonLabourTonnageRef.current.map(item => item.Year))];
    // Not sure if there is a cleaner way to
    // write the following code. Essentially it
    // is adding missing Year/ResourceGroup data.
    // The chart will fail to render correctly if
    // the missing data is not there.
    // Note: this code solution only applies to 
    // stacked bar chart.
    tonnage_years.forEach(year => {
        tonnage_resource_groups.forEach(resource => {
            const exists = nonLabourTonnageRef.current.find(i => i.Year === year && i.ResourceGroup === resource);
            if (!exists) {
                nonLabourTonnageRef.current.push({
                    ResourceGroup: resource,
                    Year: year,
                    Unit: 0
                });
            }
        });
    });

    // Sort the results from the above
    nonLabourTonnageRef.current.sort((a, b) => {
        return a.Year - b.Year || a.ResourceGroup.localeCompare(b.ResourceGroup);
    });

    // Build the datasets
    const nonlabour_tonnage_datasets = [...new Set(tonnage_resource_groups.map(resource_group => {
        return {
            label: resource_group,
            data: nonLabourTonnageRef.current.filter((obj) => obj.ResourceGroup === resource_group).map((filObj) => filObj.Unit),
            backgroundColor: nonlabour_colours.find(x => x.resourcegroup === resource_group)?.colour
        }
    }))];

    // Sort the data by the tonnage amount
    // and create the unique years array
    nonlabour_tonnage_datasets.sort((a, b) => Math.max(...b.data) - Math.max(...a.data));
    const nonlabour_tonnage_chart = {
        labels: tonnage_years,
        datasets: nonlabour_tonnage_datasets
    };
    // End - non labour tonnage chart

    useEffect(() => {
        if (nonLabourStatus == 'error') {
            console.log(nonLabourError);
        } else if (nonLabourStatus == 'success' && nonLabourFetchStatus == 'idle') {
            const records = nonLabourValuesTonnage.executeRetrieveNonLabourValueTonnage;

            nonLabourValuesRef.current = [];
            records.reduce((res, value) => {
                if (!res[value.ResourceGroup + '-' + value.Year]) {
                    res[value.ResourceGroup + '-' + value.Year] = { ResourceGroup: value.ResourceGroup, Value: 0, Year: value.Year };
                    nonLabourValuesRef.current.push(res[value.ResourceGroup + '-' + value.Year])
                }
                res[value.ResourceGroup + '-' + value.Year].Value += value.Value;
                return res;
            }, {});

            nonLabourTonnageRef.current = [];
            records.reduce((res, value) => {
                if (value.PLEMGroup === 'Material') {
                    if (!res[value.ResourceGroup + '-' + value.Year]) {
                        res[value.ResourceGroup + '-' + value.Year] = { ResourceGroup: value.ResourceGroup, Unit: 0, Year: value.Year };
                        nonLabourTonnageRef.current.push(res[value.ResourceGroup + '-' + value.Year])
                    }
                    res[value.ResourceGroup + '-' + value.Year].Unit += value.Unit;
                }
                return res;
            }, {});

            nonLabourChartLoader.current!.style.display = 'none';
            setNonLabourTab(false);
        }
    }, [nonLabourValuesTonnage, nonLabourStatus, nonLabourFetchStatus, nonLabourError]);

    useEffect(() => {
        if (updateNonLabourCharts) {
            nonLabourChartLoader.current!.style.display = 'flex';
            refetchNonLabourValuesTonnage();
        }
    }, [updateNonLabourCharts]);

    return (<Stack>
        <Box ref={nonLabourChartLoader} style={{ position: "absolute", display: "flex", zIndex: "200", width: 1000, height: 1210, justifyContent: "center", alignItems: "center", backgroundColor: "rgba(51, 51, 51, 0.4)" }}>
            <Oval
                visible={true}
                height="80"
                width="80"
                color="#005670"
                secondaryColor="#005670"
                ariaLabel="oval-loading"
                wrapperStyle={{}}
                wrapperClass=""
            />
        </Box>
        <Grid container direction="column" spacing={1}>
            <Grid item>
                <Box style={{ display: "flex", width: 1000, backgroundColor: "rgb(0, 86, 112)" }}>
                    <Box style={{ display: "flex", width: 600, color: "#FFF", fontWeight: 600, fontSize: "1.25rem", fontFamily: "ProximaNova", justifyContent: "flex-start", alignItems: "center", }}>Non-labour demand in value ($)
                    </Box>
                    <Grid container spacing={1} columns={2} sx={{ width: 400, flexGrow: 1, justifyContent: "flex-end", backgroundColor: "white" }}>
                        <MapPlotFilterList
                            AllMapLists={allMapLists}
                            plotQueryProps={nonLabourPlotQueryProps}
                            setPlotQueryProps={setNonLabourPlotQueryProps}
                            filterConfigList={nonLabourValueFiltersConfigList}
                            listsDisabled={false}
                            applyUpdates={applyUpdates} />
                    </Grid>
                </Box>
                <Grid container direction="row">
                    <Grid item>
                        <Box style={{ display: "relative", width: 800, height: 550 }}>
                            <Bar options={nonlabour_value_options} data={nonlabour_value_chart} plugins={[htmlLegend, ChartDataLabels, totaliser]} width="800" height="550" />
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box ref={valueLegendContainer} style={{ width: 195, height: 550, overflow: "auto", border: "0.7px solid #e0e0e0" }}>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Box style={{ display: "flex", width: 1000, backgroundColor: "rgb(0, 86, 112)" }}>
                    <Box style={{ display: "flex", width: 800, color: "#FFF", fontWeight: 600, fontSize: "1.25rem", fontFamily: "ProximaNova", justifyContent: "flex-start", alignItems: "center", }}>Non-labour demand in tonnage
                    </Box>
                    <Grid container spacing={1} columns={1} sx={{ width: 200, flexGrow: 1, justifyContent: "flex-end", backgroundColor: "white" }}>
                        <MapPlotFilterList
                            AllMapLists={allMapLists}
                            plotQueryProps={nonLabourPlotQueryProps}
                            setPlotQueryProps={setNonLabourPlotQueryProps}
                            filterConfigList={nonLabourTonnageFiltersConfigList}
                            listsDisabled={false}
                            applyUpdates={applyUpdates} />
                    </Grid>
                </Box>
                <Grid container direction="row">
                    <Grid item>
                        <Box style={{ display: "relative", width: 800, height: 550 }}>
                            <Bar options={nonlabour_tonnage_options} data={nonlabour_tonnage_chart} plugins={[htmlLegend, ChartDataLabels, totaliser]} width="800" height="550" />
                        </Box>
                    </Grid>
                    <Grid item>
                        <Box ref={tonnageLegendContainer} style={{ width: 195, height: 550, overflow: "auto", border: "0.7px solid #e0e0e0" }}>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    </Stack >)
}